import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Screen } from '@ghostscreen/contracts';
import { DeviceCommandStatus } from '../../fleet/DeviceCommandStatus';
import {
  buildFixVisibility,
  buildStatusLine,
  FIX_COMMANDS,
  fixButtonLabel,
  statusColorVar,
  statusLabel,
} from './buildFixScreenModel';
import { useScreenFixCommands } from './useScreenFixCommands';

type FixThisScreenSectionProps = {
  organisationId: string;
  screen: Screen;
};

/**
 * Paired-display status strip + Fix this screen
 * (Ghost Screen Displays.dc.html).
 */
export function FixThisScreenSection({ organisationId, screen }: FixThisScreenSectionProps) {
  const { t } = useTranslation(['displays', 'common']);
  const {
    connection,
    active,
    busy,
    anyQueued,
    error,
    enqueuePending,
    run,
    clearLocal,
    onStatusChange,
  } = useScreenFixCommands(organisationId, screen);

  const [manuallyOpen, setManuallyOpen] = useState(false);

  // Offline / unknown always force the section open; clear manual flag when
  // attention clears so online+idle collapses again after a successful fix.
  useEffect(() => {
    if (connection === 'offline' || connection === 'unknown') {
      setManuallyOpen(false);
    }
  }, [connection, screen.id]);

  const visibility = buildFixVisibility({
    connection,
    busy,
    anyQueued,
    manuallyOpen,
  });

  const live = connection === 'online';
  const color = statusColorVar(connection);

  return (
    <div className="flex flex-col gap-3">
      <div
        className="flex flex-wrap items-center gap-3 rounded-[14px] border px-[18px] py-4"
        style={{ background: 'var(--gs-panel2)', borderColor: 'var(--gs-line)' }}
      >
        <span
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-[12.5px] font-bold"
          style={{
            color,
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
          }}
        >
          <span className="h-[7px] w-[7px] rounded-full" style={{ background: color }} aria-hidden />
          {statusLabel(connection)}
        </span>
        <span
          className="min-w-[200px] flex-1 text-sm"
          style={{ color: 'var(--gs-ink2)', lineHeight: 1.45 }}
        >
          {buildStatusLine(connection, screen.lastHeartbeatAt)}
        </span>
        {visibility.showLink ? (
          <button
            type="button"
            onClick={() => setManuallyOpen(true)}
            className="shrink-0 bg-transparent p-1 text-[13px] font-semibold underline"
            style={{ color: 'var(--gs-muted)', border: 'none', fontFamily: 'inherit' }}
          >
            {t('fix.looksWrong')}
          </button>
        ) : null}
      </div>

      {visibility.showSection ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span
              className="text-[17px] font-extrabold tracking-[-0.015em]"
              style={{ color: 'var(--gs-ink)' }}
            >
              {t('fix.title')}
            </span>
            <span
              className="text-[13.5px]"
              style={{ color: 'var(--gs-muted)', lineHeight: 1.45, textWrap: 'pretty' }}
            >
              {visibility.intro}
            </span>
          </div>

          <div
            className="flex flex-col overflow-hidden rounded-[14px] border"
            style={{ borderColor: 'var(--gs-line)' }}
          >
            {FIX_COMMANDS.map((def, index) => {
              const isActive = active?.kind === def.kind;
              const phase = active?.phase;
              const showBusy = isActive && (busy || enqueuePending);
              const queued = isActive && phase === 'queued';

              return (
                <div
                  key={def.kind}
                  className="flex flex-wrap items-center gap-3 px-4 py-3.5"
                  style={{
                    borderTop: index ? '1px solid var(--gs-line)' : undefined,
                  }}
                >
                  <div className="flex min-w-[180px] flex-1 flex-col gap-[3px]">
                    <span className="text-[14.5px] font-bold" style={{ color: 'var(--gs-ink)' }}>
                      {t(`fix.commands.${def.kind}.label`)}
                    </span>
                    <span
                      className="text-[13px]"
                      style={{ color: 'var(--gs-muted)', lineHeight: 1.4 }}
                    >
                      {t(`fix.commands.${def.kind}.description`)}
                    </span>
                  </div>

                  {!showBusy ? (
                    <button
                      type="button"
                      disabled={!screen.deviceId || enqueuePending}
                      onClick={() => run(def.kind)}
                      className="shrink-0 rounded-[9px] border px-3.5 py-[9px] text-[13.5px] font-bold disabled:opacity-50"
                      style={{
                        color: 'var(--gs-ink)',
                        background: 'var(--gs-panel)',
                        borderColor: 'var(--gs-line)',
                        fontFamily: 'inherit',
                      }}
                    >
                      {fixButtonLabel(def, live)}
                    </button>
                  ) : (
                    <span
                      className="inline-flex shrink-0 items-center gap-[7px] text-[13px] font-bold"
                      style={{
                        color: queued ? 'var(--gs-warn)' : 'var(--gs-accent)',
                      }}
                    >
                      <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{
                          background: queued ? 'var(--gs-warn)' : 'var(--gs-accent)',
                          animation: 'gs-pulse 1.4s ease-in-out infinite',
                        }}
                        aria-hidden
                      />
                      {queued ? t('fix.waitingForScreen') : t('common:actions.working')}
                    </span>
                  )}

                  {queued ? (
                    <button
                      type="button"
                      onClick={clearLocal}
                      className="shrink-0 bg-transparent px-1 py-1.5 text-[13px] font-semibold underline"
                      style={{ color: 'var(--gs-muted)', border: 'none', fontFamily: 'inherit' }}
                    >
                      {t('common:actions.cancel')}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {active ? (
            <DeviceCommandStatus
              organisationId={organisationId}
              deviceId={active.deviceId}
              commandId={active.commandId}
              commandType={active.type}
              onStatusChange={onStatusChange}
            />
          ) : null}

          {error ? (
            <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
              {error}
            </p>
          ) : null}

          {visibility.canHide ? (
            <button
              type="button"
              onClick={() => setManuallyOpen(false)}
              className="self-start bg-transparent py-0.5 text-[13px] font-semibold"
              style={{ color: 'var(--gs-muted)', border: 'none', fontFamily: 'inherit' }}
            >
              {t('fix.hide')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
